import type { LocalCalendarEntry } from 'owa-graph-schema';
import type { CustomData } from 'owa-analytics-actions';
import { isConnectedAccount } from 'owa-accounts-store';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { areAnyTXPEntitiesSupported } from 'owa-txp-common';
import { isEventAMeeting, canRespond, canRemove } from 'owa-calendar-event-capabilities';
import { logUsage } from 'owa-analytics';
import { default as getTimeRelevance } from 'owa-datetime-utils/lib/getTimeRelevance';
import type { ReadingPaneEntrySourceType } from '../schema/ReadingPaneEntrySourceType';
import type { ViewType } from 'owa-calendar-actions';
import { getCalendarEntryByFolderId } from 'owa-calendar-cache';
import { hasResponded } from 'owa-event-context-menu';
import { CalendarComponent } from 'owa-calendar-usage-instrumentation';

type EventActionNecessary = 'None' | 'ResponseRequired' | 'Cancellation';

/**
 * @param isPeek Boolean indicating if the event is viewed from item peek or reading pane.
 * @param item The item being viewed.
 * @param readingPaneEntrySource The user's entry source for opening the reading pane.
 */
export default function (
    viewType: ViewType,
    item: CalendarEvent,
    component: CalendarComponent = CalendarComponent.Unknown,
    entrySource: ReadingPaneEntrySourceType = 'Unspecified'
) {
    // TODO VSO:46662 - Update entrySource to be generic for all view types and add new values

    const experienceType = item.ExperienceType || 'Unspecified';
    const isTxp = areAnyTXPEntitiesSupported(item.EntityNamesMap);

    let customData: CustomData = {
        component: component,
        viewType: viewType,
        entrySource: entrySource,
        experienceType: experienceType,
        isTxp: isTxp,
        isOrganizer: item.IsOrganizer,
        isMeeting: isEventAMeeting(item),
        timeRelevance: getTimeRelevance(item.Start, item.End),
        hasAttachments: item.HasAttachments,
        actionNecessary: getActionNecessary(item),
        hasResponded: canRespond(item) && hasResponded(item),
    };

    if (isConnectedAccount(item?.ParentFolderId?.mailboxInfo?.userIdentity)) {
        customData = { ...customData, MailboxType: 'Connected' };
    }

    if (isSchoolCalendarEvent(item)) {
        customData = { ...customData, CalendarType: 'School' };
    }

    logUsage('ViewCalendarEvent', customData, {
        isCore: true,
    });
}

function getActionNecessary(item: CalendarEvent): EventActionNecessary {
    if (canRespond(item) && !hasResponded(item)) {
        return 'ResponseRequired';
    } else if (canRemove(item)) {
        return 'Cancellation';
    }

    return 'None';
}

function isSchoolCalendarEvent(item: CalendarEvent): boolean {
    const calendarEntry: LocalCalendarEntry = getCalendarEntryByFolderId(
        item?.ParentFolderId?.Id
    ) as LocalCalendarEntry;
    return calendarEntry?.IsSchoolCalendar;
}
